import React, { useMemo } from 'react'
import { getReportDueDate } from '../reportDueDate'
import clsx from 'clsx'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json'
import traslation from '../../../components/modules/ui/traslation/traslationAdministracion.json'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

// Helper para detectar notas de crédito
const isCreditNote = (nombre, status) => {
  const nf = String(nombre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
  return (
    nf === '61' ||
    nf === 'nce' ||
    (nf.includes('nota') && nf.includes('credito')) ||
    Boolean(extractAnnulledFolio(status))
  )
}

const extractAnnulledFolio = status => {
  const match = String(status || '')
    .toUpperCase()
    .match(/ANULA\s+FOLIO\s+(\d+)/)
  return match?.[1] || null
}

const extractCancelledByCreditNoteFolio = status => {
  const match = String(status || '')
    .toUpperCase()
    .match(/ANULAD[OA]\s+POR\s+NC\s+(\d+)/)
  return match?.[1] || null
}

export default function TableMovimientos({ 
  data = [], 
  className,
  lastElementRef, 
  hasNextPage, 
  isFetchingNextPage
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const headers = [
    'RUT',
    'Razón Social',
    'Documento',
    'Folio',
    'Fecha',
    'Fecha vencimiento',
    'Vencimiento',
    'Días',
    'Monto',
    'Monto saldado',
    'Saldo Documento',
    'Estado'
  ]

  const formatDMY = isoString => {
    if (!isoString) return ''
    return isoString.slice(0, 10).split('-').reverse().join('/')
  }

  const obtenerAbreviatura = nombre =>
    traslation.documentosContables[nombre] || nombre

  // 1) Preprocesar linkage
  const linkageSequences = useMemo(() => {
    const map = new Map()
    data.forEach(mov => {
      mov.linkage?.forEach(link => {
        const list = map.get(link.id) || []
        list.push({
          movId: mov.id,
          folio: mov.numeroFolio,
          montoAbono: link.montoAbono ?? 0,
          montoRelacionado: link.montoRelacionado ?? 0,
          fechaPago: link.fechaPago,
          banco: link.nombreCuentaBanco || ''
        })
        map.set(link.id, list)
      })
    })
    map.forEach(entries => {
      entries.sort((a, b) => a.movId - b.movId)
      let prevSaldo = 0
      entries.forEach((e, idx) => {
        const abono = e.montoAbono
        const cobro = e.montoRelacionado
        const saldo = idx === 0 ? abono - cobro : prevSaldo - cobro
        e.abono = abono
        e.cobroAplicado = cobro
        e.saldo = saldo
        prevSaldo = saldo
      })
    })
    return map
  }, [data])

  // 2) Construir filas procesadas
  const processedRows = useMemo(() => {
    return data.map(mov => {
      const {
        id: movId,
        rut,
        razonSocial,
        nombreFolio,
        numeroFolio,
        fecha: issuedDate,
        fechaVencimiento,
        montoTotal = 0,
        status = '',
        linkage = []
      } = mov

      const isNC = isCreditNote(nombreFolio, status)
      const folioAnulado = isNC ? extractAnnulledFolio(status) : null
      let filas = []

      if (isNC) {
        // Nota de crédito -> cobro aplicado completo
        filas = [{
          folio: numeroFolio,
          abono: 0,
          cobroAplicado: montoTotal,
          saldo: 0,
          fechaPago: null,
          banco: ''
        }]
      } else {
        // Lógica estándar de linkage
        linkage.forEach(link => {
          const seq = linkageSequences.get(link.id) || []
          seq.forEach(item => {
            if (item.movId === movId && item.folio === numeroFolio) {
              filas.push(item)
            }
          })
        })

        // Agrupar múltiples vinculaciones
        if (filas.length > 1) {
          const abonoSum = filas.reduce((sum, f) => sum + (f.abono ?? 0), 0)
          const cobroSum = filas.reduce((sum, f) => sum + (f.cobroAplicado ?? 0), 0)
          const saldoLast = filas[filas.length - 1].saldo ?? 0
          filas = [{
            folio: numeroFolio,
            abono: abonoSum,
            cobroAplicado: cobroSum,
            saldo: saldoLast,
            fechaPago: null,
            banco: ''
          }]
        }

        // Sin vínculo
        if (filas.length === 0) {
          filas = [{
            folio: numeroFolio,
            abono: 0,
            cobroAplicado: 0,
            saldo: montoTotal,
            fechaPago: null,
            banco: ''
          }]
        }

        // Anulados sin ser nota de crédito -> cero cobro, saldo completo
        if (String(status).toLowerCase().includes('anul')) {
          filas = [{
            folio: numeroFolio,
            abono: 0,
            cobroAplicado: 0,
            saldo: montoTotal,
            fechaPago: null,
            banco: ''
          }]
        }
      }

      const fechaDoc = formatDMY(issuedDate)

      const { vencimiento, dias } = getReportDueDate(fechaVencimiento)

      return {
        movId,
        rut,
        razonSocial,
        documento: obtenerAbreviatura(nombreFolio),
        folio: numeroFolio,
        folioAnulado,
        fecha: fechaDoc,
        fechaVencimiento: formatDMY(fechaVencimiento),
        vencimiento,
        dias,
        montoTotal,
        estado: status,
        filas,
        isNC
      }
    })
  }, [data, linkageSequences])

  // 3) Agrupar por RUT y calcular subtotales excluyendo NC
  const groupedByRut = useMemo(() => {
    const map = new Map()
    processedRows.forEach(r => {
      if (!map.has(r.rut)) {
        map.set(r.rut, { rut: r.rut, razonSocial: r.razonSocial, items: [], subtotal: 0 })
      }
      const grp = map.get(r.rut)
      grp.items.push(r)
      if (!r.isNC) grp.subtotal += r.montoTotal
    })
    map.forEach(grp => {
      const documentosPorFolio = new Map(
        grp.items
          .filter(item => !item.isNC)
          .map(item => [String(item.folio), item])
      )
      grp.items = grp.items.map(item => ({
        ...item,
        saldaEnGrupo: !item.isNC || item.folioAnulado === '0' || (() => {
          const documentoAnulado = documentosPorFolio.get(String(item.folioAnulado))
          const folioNCEnDocumento = extractCancelledByCreditNoteFolio(documentoAnulado?.estado)

          return Boolean(documentoAnulado && folioNCEnDocumento === String(item.folio))
        })()
      }))
      grp.items.sort((a, b) => Number(a.fecha) - Number(b.fecha))
    })
    return Array.from(map.values())
  }, [processedRows])

  const getCobroAplicado = row =>
    row.saldaEnGrupo ? (row.filas[0]?.cobroAplicado ?? 0) : 0

  const getCobroInformado = row =>
    row.filas[0]?.cobroAplicado ?? 0

  // 4) Totales generales excluyendo NC
  const totalMonto = groupedByRut.reduce((sum, grp) => sum + grp.subtotal, 0)
  const totalCobro = groupedByRut.reduce((sum, grp) =>
    sum + grp.items.reduce((acc, r) => acc + getCobroAplicado(r), 0)
  , 0)
  const totalSaldo = totalMonto - totalCobro

  const formatCLP = v =>
    v.toLocaleString('es-CL', { minimumFractionDigits: 0 })

  if (!groupedByRut.length) {
    return <div className='py-12 text-center text-muted font-sans'>No hay datos</div>
  }

  if (isMobile) {
    const renderMetric = (label, value) => (
      <div className="rounded-lg bg-surface/70 border border-border px-2 py-2 min-w-0">
        <div className="text-[11px] text-muted">{label}</div>
        <div className="text-sm font-semibold text-text break-words">{value}</div>
      </div>
    )

    return (
      <div className={clsx('space-y-3 font-sans text-text', className)}>
        {groupedByRut.map((grp, gi) => (
          <React.Fragment key={gi}>
            <div className="rounded-2xl border border-border bg-surface/70 p-3">
              <div className="text-xs font-semibold text-text">
                {grp.rut} {grp.razonSocial}
              </div>
              <div className="mt-2">
                {renderMetric('Subtotal', formatCLP(grp.subtotal))}
              </div>
            </div>

            {grp.items.map((r, ri) => {
              const cobro = getCobroAplicado(r)
              const cobroInformado = getCobroInformado(r)
              const mostrarCobro = cobroInformado > 0
              const saldo = r.isNC ? '' : r.montoTotal - cobro
              const diasLabel = r.dias !== '' ? `${r.dias} días` : ''

              return (
                <div key={`${gi}-${ri}`} className="rounded-2xl border border-border bg-surface p-3">
                  <div className="text-xs font-semibold text-text">
                    {r.documento} {r.folio}
                  </div>
                  <div className="mt-1 text-[11px] text-muted">
                    {r.fecha} {r.vencimiento ? `· ${r.vencimiento}` : ''} {diasLabel ? `· ${diasLabel}` : ''}
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {renderMetric('Fecha vencimiento', r.fechaVencimiento)}
                    {renderMetric('Monto', r.isNC ? '' : formatCLP(r.montoTotal))}
                    {renderMetric('Monto saldado', mostrarCobro ? formatCLP(cobroInformado) : '')}
                    {renderMetric('Saldo', saldo !== '' ? formatCLP(saldo) : '')}
                    {renderMetric('Estado', r.estado)}
                  </div>
                </div>
              )
            })}
          </React.Fragment>
        ))}

        <div className="rounded-2xl border border-border bg-surface p-3">
          <div className="text-xs font-semibold text-text">Total</div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {renderMetric('Monto', formatCLP(totalMonto))}
            {renderMetric('Monto saldado', formatCLP(totalCobro))}
            {renderMetric('Saldo', formatCLP(totalSaldo))}
          </div>
        </div>

        {hasNextPage && (
          <div ref={lastElementRef} className="flex justify-center py-4 flex-shrink-0">
            {isFetchingNextPage && (<Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`overflow-y-auto overflow-x-auto border border-border rounded bg-surface font-sans text-[11px] sm:text-xs text-text leading-5 ${className}`}>
      <table className='table-report-auto min-w-full' style={{ tableLayout: 'auto' }}>
        <thead className='sticky top-0 bg-surface'>
          <tr>
            {headers.map(col => {
              const isDocumento = col === 'Documento'
              const isRazonSocial = col === 'Razón Social'
              const isMontoSaldado = col === 'Monto saldado'
              const isSaldoDocumento = col === 'Saldo Documento'
              const allowWrap = isDocumento || isRazonSocial || isMontoSaldado || isSaldoDocumento
              const widthClass = isDocumento
                ? 'w-[68px]'
                : isRazonSocial
                  ? 'w-[120px]'
                  : isMontoSaldado
                    ? 'w-[90px]'
                    : isSaldoDocumento
                      ? 'w-[95px]'
                      : ''
              return (
                <th
                  key={col}
                  className={`table-report-th text-center leading-tight ${widthClass} ${
                    allowWrap ? 'whitespace-normal break-words' : 'whitespace-nowrap'
                  }`}
                >
                  {col}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {groupedByRut.map((grp, gi) => (
            <React.Fragment key={gi}>
              {grp.items.map((r, ri) => {
                const cobro = getCobroAplicado(r)
                const cobroInformado = getCobroInformado(r)
                const mostrarCobro = cobroInformado > 0
                const saldo = r.isNC ? '' : r.montoTotal - cobro

                return (
                  <tr key={ri} className='table-report-row'>
                    <td className='table-report-td text-center whitespace-nowrap'>{r.rut}</td>
                    <td className='table-report-td text-center max-w-[120px] whitespace-normal break-words leading-tight'>{r.razonSocial}</td>
                    <td className='table-report-td text-center max-w-[68px] whitespace-normal break-all leading-tight'>{r.documento}</td>
                    <td className='table-report-td text-center'>{r.folio}</td>
                    <td className='table-report-td text-center'>{r.fecha}</td>
                    <td className='table-report-td text-center whitespace-nowrap'>{r.fechaVencimiento}</td>
                    <td className='table-report-td text-center'>{r.vencimiento}</td>
                    <td className='table-report-td text-center'>{r.dias}</td>
                    <td className={clsx('table-report-td text-center', r.montoTotal < 0 && 'text-danger')}>
                      {r.isNC ? '' : formatCLP(r.montoTotal)}
                    </td>
                    <td className='table-report-td text-center whitespace-nowrap'>{mostrarCobro ? formatCLP(cobroInformado) : ''}</td>
                    <td className='table-report-td text-center whitespace-nowrap'>{saldo !== '' ? formatCLP(saldo) : ''}</td>
                    <td className='table-report-td text-center whitespace-normal break-words leading-tight'>{r.estado}</td>
                  </tr>
                )
              })}
              <tr className='table-report-section'>
                <td className='table-report-td text-left'>Subtotal</td>
                <td colSpan={7} />
                <td className='table-report-td text-center'>{formatCLP(grp.subtotal)}</td>
                <td className='table-report-td text-center'>
                  {formatCLP(
                    grp.items.reduce((s, r) => s + getCobroAplicado(r), 0)
                  )}
                </td>
                <td className='table-report-td text-center'>
                  {formatCLP(
                    grp.subtotal - grp.items.reduce((s, r) => s + getCobroAplicado(r), 0)
                  )}
                </td>
                <td />
              </tr>
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr className='table-report-total'>
            <td className='table-report-td text-left'>Total</td>
            <td colSpan={7} />
            <td className='table-report-td text-center'>{formatCLP(totalMonto)}</td>
            <td className='table-report-td text-center'>{formatCLP(totalCobro)}</td>
            <td className='table-report-td text-center'>{formatCLP(totalSaldo)}</td>
            <td />
          </tr>
        </tfoot>
      </table>

      {/* Infinite scroll trigger */}
      {hasNextPage && (
        <div ref={lastElementRef} className="flex justify-center py-4 flex-shrink-0">
          {isFetchingNextPage && (<Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />)}
        </div>
      )}
    </div>
  )
}
